import { decodePeriodValue, GlobalPeriodSelect, GlobalPeriodSelectProps } from '@components'

import { FormItem, Space } from '@billy/design-system'
import { differenceInYears, endOfYear, setYear } from 'date-fns'
import React, { ReactElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { PeriodMode } from '../../../../enums/periodMode'
import { ReportsComparisonPeriodSelect, ReportsComparisonPeriodSelectProps } from '../ReportsComparisonPeriodSelect'
import { ReportsComparisonSelect, ReportsComparisonSelectProps } from '../ReportsComparisonSelect'
import * as Styled from './styles'

export interface ReportsPeriodFiltersProps {
  comparisonId?: ReportsComparisonSelectProps['selectedId']
  comparisonPeriodId?: ReportsComparisonPeriodSelectProps['selectedId']
  disabledPeriodModes?: GlobalPeriodSelectProps['disabledPeriodModes']
  encodedPeriod?: GlobalPeriodSelectProps['encodedPeriod']
  isComparisonSelectDisabled?: boolean
  maxDate?: GlobalPeriodSelectProps['maxDate']
  minDate?: GlobalPeriodSelectProps['minDate']
  onComparisonPeriodSelect?: ReportsComparisonPeriodSelectProps['onSelect']
  onComparisonSelect?: ReportsComparisonSelectProps['onSelect']
  onPeriodChange?: GlobalPeriodSelectProps['onChange']
  reportsComparisonSelectItems?: ReportsComparisonSelectProps['items']
  requireFinitePeriods?: GlobalPeriodSelectProps['requireFinitePeriods']
  withPeriodComparison?: boolean
  withLabels?: boolean
}

export const ReportsPeriodFilters = ({
  comparisonId,
  comparisonPeriodId,
  disabledPeriodModes: disabledPeriodModesControlled = [],
  encodedPeriod,
  isComparisonSelectDisabled,
  maxDate,
  minDate,
  onComparisonPeriodSelect,
  onComparisonSelect,
  onPeriodChange,
  reportsComparisonSelectItems,
  requireFinitePeriods,
  withPeriodComparison = true,
  withLabels = true,
}: ReportsPeriodFiltersProps): ReactElement => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()

  const disabledPeriodModes: PeriodMode[] = useMemo(
    () => [PeriodMode.All, ...disabledPeriodModesControlled],
    [disabledPeriodModesControlled],
  )

  const periodDecoded = useMemo(() => (encodedPeriod ? decodePeriodValue(encodedPeriod) : undefined), [encodedPeriod])

  // Check what max periods amount should be possible to select - it's used to block showing too many fiscal years
  const maxPeriods = useMemo(() => {
    if (periodDecoded?.mode !== PeriodMode.FiscalYear || !organization?.firstFiscalYearStart) {
      return undefined
    }

    const dateToCompare = periodDecoded.year !== undefined ? setYear(new Date(), periodDecoded.year) : new Date()
    const firstFiscalYearStart = new Date(organization.firstFiscalYearStart)
    return differenceInYears(endOfYear(dateToCompare), endOfYear(firstFiscalYearStart))
  }, [organization?.firstFiscalYearStart, periodDecoded])

  return (
    <Styled.ReportsPeriodFiltersWrapper>
      <FormItem label={withLabels ? t('reports.filters.period') : undefined}>
        <GlobalPeriodSelect
          disabledPeriodModes={disabledPeriodModes}
          encodedPeriod={encodedPeriod}
          fullWidth
          maxDate={maxDate}
          minDate={minDate}
          onChange={onPeriodChange}
          requireFinitePeriods={requireFinitePeriods}
        />
      </FormItem>
      <Space size="s8" horizontal />
      <FormItem label={withLabels ? t('reports.filters.comparison') : undefined}>
        <Styled.ReportsComparisonSelectWrapper>
          <ReportsComparisonSelect
            disabled={isComparisonSelectDisabled}
            fullWidth
            items={reportsComparisonSelectItems}
            onSelect={onComparisonSelect}
            periodMode={periodDecoded?.mode}
            selectedId={comparisonId}
          />
        </Styled.ReportsComparisonSelectWrapper>
      </FormItem>

      {withPeriodComparison && (
        <>
          <Space size="s8" horizontal />
          <FormItem label={withLabels ? t('reports.filters.periods_count') : undefined}>
            <Styled.ReportsComparisonPeriodSelect>
              <ReportsComparisonPeriodSelect
                fullWidth
                maxPeriods={maxPeriods}
                onSelect={onComparisonPeriodSelect}
                selectedId={comparisonPeriodId}
              />
            </Styled.ReportsComparisonPeriodSelect>
          </FormItem>
        </>
      )}
    </Styled.ReportsPeriodFiltersWrapper>
  )
}
